import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FormControl } from '@angular/forms';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import { FilterBase } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-base';
import {
  DateTimeRangeValue,
  FilterDateRange,
} from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-datetime-range.model';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { createDatetimeRangeValidator } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/datetime-validator';

@Component({
  standalone: false,
  selector: 'nimbus-filter-datetime-range',
  templateUrl: './nimbus-filter-datetime-range.component.html',
  styleUrls: ['./nimbus-filter-datetime-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusFilterDatetimeRangeComponent extends FilterBase<DateTimeRangeValue, FilterDateRange> implements OnInit {
  @Input() public locale?: string;

  protected valuesEmpty: DateTimeRangeValue[] = [
    { start: null, end: null },
    { start: null, end: null, invalid: false },
  ];

  public formControl: FormControl;
  public minDate = moment({
    year: 2020,
    month: 0,
    day: 1,
  }).toDate();
  public maxDate = moment().endOf('days').toDate();
  public hasError: boolean = false;

  public constructor(
    private readonly translateService: TranslateService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super();

    this.formControl = new FormControl<DateTimeRangeValue>(
      { start: null, end: null },
      createDatetimeRangeValidator(this.minDate, this.maxDate),
    );
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.minDate = this.filter.minDate || this.minDate;
    this.maxDate = this.filter.maxDate || this.maxDate;
  }

  public onDetach(): void {
    this.filterValue = cloneDeep(this.formControl.value);
    this.filterValue.end =
      this.filterValue?.end || (this.filterValue?.start ? moment(this.filterValue.start).endOf('day').toISOString() : null);
    this.filterValueChanged.emit({ ...this.filterValue, invalid: this.hasError });
    this.summary = this.getSummary();
    this.changeDetectorRef.detectChanges();
  }

  protected getSummary(): string {
    if (!this.filterValue || (!this.filterValue?.start && !this.filterValue?.end)) {
      this.hasError = false;
      return this.translateService.instant(TranslationKeys.FilterAny);
    }

    this.formControl.updateValueAndValidity();
    this.hasError = this.formControl.invalid;
    if (this.hasError) {
      this.hasError = true;
      return this.formControl.errors?.between
        ? `Selected period should be between ${this.locale ? moment(this.minDate).locale(this.locale).format(this.filter.dateFormat) : moment(this.minDate).format(this.filter.dateFormat)} and ${
            this.locale
              ? moment(this.maxDate).locale(this.locale).format(this.filter.dateFormat)
              : moment(this.minDate).format(this.filter.dateFormat)
          } `
        : `Invalid Date and Time Range Selected`;
    }

    const format = `${this.filter.dateFormat} ${this.filter.timeFormat}`;
    const startDate = this.filterValue?.start;
    const endDate = this.filterValue?.end;
    return startDate && !endDate
      ? `${this.locale ? moment(startDate).locale(this.locale).format(format) : moment(startDate).format(format)} - ${this.translateService.instant(TranslationKeys.FilterAny)}`
      : `${this.locale ? moment(startDate).locale(this.locale).format(format) : moment(startDate).format(format)} - ${this.locale ? moment(endDate).locale(this.locale).format(format) : moment(endDate).format(format)}`;
  }

  protected onFilterValueChanged(): void {
    this.formControl.setValue({
      start: this.filterValue?.start,
      end: this.filterValue?.end || (this.filterValue?.start ? moment(this.filterValue.start).endOf('day').toISOString() : null),
    });
  }
}
